import React from "react";
import { BsArrowLeft } from "react-icons/bs";
import { FiCopy, FiExternalLink } from "react-icons/fi";
import CopyToClipboard from "../../components/utils/CopyToClipboard";
import { formatAmount } from "../../utils/formatTime";
import {
  getTransactionStatusColor,
  shouldMaskFailedAsPending,
} from "../../utils/transactionStatus";
import { getDetailFields } from "../../utils/activity/detailFields";

/**
 * In-app transaction detail view — the step before the shareable receipt,
 * mirroring the app's /transaction-details → /transaction-receipt flow.
 *
 * The receipt is a branded artifact built to leave the platform; this screen is
 * where the full record lives, including fields the receipt deliberately omits.
 */

const headlineAmount = (tx: any): string => {
  switch (tx?.transactionType) {
    case "payouts":
      return `₦${formatAmount(tx.payoutAmount ?? 0)}`;
    case "gift-card-transactions":
      return `$${formatAmount(
        (tx.tradeValue ?? []).reduce(
          (total: number, item: any) => total + (item.amount ?? 0),
          0
        )
      )}`;
    case "deex-asset-swaps":
      return `${formatAmount(tx.from?.amount ?? 0)} ${(
        tx.from?.asset ?? ""
      ).toUpperCase()}`;
    case "deex-crypto-sells":
      return `${formatAmount(tx.amount ?? 0)} ${(
        tx.sellAsset ??
        tx.asset ??
        ""
      )
        .toString()
        .toUpperCase()}`;
    default:
      return tx?.amount != null
        ? `${formatAmount(tx.amount)} ${(tx.currency ?? "").toUpperCase()}`
        : "";
  }
};

const prettyType = (transactionType?: string) =>
  (transactionType ?? "")
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * The receipt is a proof-of-completion artifact, so it's only offered once the
 * transaction has actually settled — a "receipt" for a failed withdrawal is
 * something a user could present as proof of payment.
 */
const canShareReceipt = (tx: any) => {
  const status = tx?.status?.toLowerCase();
  if (!status) return true; // deposits and legacy bill rows carry no status
  return status === "completed" || status === "successful" || status === "success";
};

const TransactionDetailsModal = ({
  transaction,
  onClose,
  onViewReceipt,
}: {
  transaction: any;
  onClose: () => void;
  onViewReceipt: () => void;
}) => {
  const fields = getDetailFields(transaction);
  const masked = shouldMaskFailedAsPending(transaction);
  const statusLabel = masked ? "PENDING" : transaction?.status ?? "COMPLETED";

  const reportUrl = `https://api.whatsapp.com/send?phone=2348163350001&text=Reporting%20Transaction%20ID%20-%20${transaction?._id}`;

  return (
    <div className="fixed inset-0 z-50 font-manrope flex justify-center items-start pt-10 overflow-auto pb-10 bg-black bg-opacity-75">
      <div className="w-[92%] lgss:w-[520px] bg-[#F4F6F8] dark:bg-[#13181B] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onClose} aria-label="Close">
            <BsArrowLeft className="text-[22px] text-gray-900 dark:text-gray-100" />
          </button>
          <h4 className="text-[17px] font-bold text-gray-900 dark:text-gray-100">
            Transaction details
          </h4>
          <span className="w-[22px]" />
        </div>

        <div className="w-full bg-white dark:bg-[#1C2227] rounded-xl px-4 py-6 flex flex-col items-center">
          <span
            className={`text-[12px] font-bold ${getTransactionStatusColor(
              transaction?.status,
              transaction
            )}`}
          >
            {statusLabel}
          </span>
          <h4 className="font-bold text-[24px] text-gray-900 dark:text-gray-100 mt-1">
            {headlineAmount(transaction)}
          </h4>
          <span className="text-[12px] text-gray-500 dark:text-gray-400 mt-1">
            {prettyType(transaction?.transactionType)}
          </span>
        </div>

        <div className="w-full bg-white dark:bg-[#1C2227] rounded-xl px-4 py-2 mt-3">
          {fields.map((field, i) => (
            <div
              key={`${field.label}-${i}`}
              className="flex justify-between items-start gap-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0"
            >
              <span className="text-gray-500 dark:text-gray-400 text-[13px] shrink-0">
                {field.label}
              </span>
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  className={`text-gray-900 dark:text-gray-100 text-[13px] font-semibold text-right truncate ${
                    field.mono ? "font-mono" : ""
                  }`}
                >
                  {field.href ? (
                    <a
                      href={field.href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-secondary_button inline-flex items-center gap-1"
                    >
                      {field.value}
                      <FiExternalLink className="shrink-0" />
                    </a>
                  ) : (
                    field.value
                  )}
                </span>
                {field.copyable && (
                  <CopyToClipboard text={field.value}>
                    <FiCopy className="text-[14px] text-gray-400 hover:text-secondary_button shrink-0" />
                  </CopyToClipboard>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 mt-5">
          {canShareReceipt(transaction) && (
            <button
              onClick={onViewReceipt}
              className="w-full py-3 rounded-xl bg-secondary_button text-white text-[14px] font-semibold"
            >
              View receipt
            </button>
          )}
          <a
            href={reportUrl}
            target="_blank"
            rel="noreferrer"
            className="w-full py-3 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-[14px] font-semibold text-center"
          >
            Report transaction
          </a>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;
